import { fieldSubscriptionItems } from 'final-form';
import React from 'react';
import { Field } from 'react-final-form';
import { useIntl } from 'react-intl';

import { Ellipsis, Form } from 'ui/base';

const DEFAULT_SUBSCRIPTION = fieldSubscriptionItems.reduce((acc, field) => ({
  ...acc,
  [field]: field !== 'active'
}), {});

/**
 * @template T
 * @typedef {{
 *   formItemProps?: Omit<import("antd").FormItemProps, 'label' | 'help' | 'validateStatus' | 'required'> & { 'data-cy'?: string },
 *   label?: React.ReactNode,
 *   labelId?: string,
 *   labelParameters?: unknown[],
 *   errorParameters?: Record<string, number>,
 *   inputOnChange?: (event: unknown) => void,
 *   error?: string,
 *   hidden?: boolean,
 *   defaultTranslation?: string,
 *   validateOnInit?: boolean,
 *   hideError?: boolean,
 *   className?: string,
 *   allowNull?: boolean,
 *   disabled?: boolean,
 *   children?: (input: import("react-final-form").FieldInputProps<T> & { disabled?: boolean }) => React.ReactNode,
 * } & Omit<import("react-final-form").FieldProps<T>, 'children' | 'render' | 'component'> &
 *   Pick<import("antd").FormItemProps, 'required'>
 * } FormFieldProps
 */

/** @type {React.FC<FormFieldProps<T>>} */
const FormField = props => {
  const {
    name,
    labelId,
    labelParameters = [],
    errorParameters = {},
    type,
    subscription = DEFAULT_SUBSCRIPTION,
    validate,
    validateFields,
    initialValue,
    inputOnChange,
    children,
    required,
    value,
    error,
    hidden = false,
    label: labelProp,
    format,
    formatOnBlur,
    defaultTranslation: defaultMessage,
    validateOnInit = false,
    parse,
    hideError,
    className,
    allowNull = false,
    formItemProps = {},
    disabled,
  } = props;

  const intl = useIntl();

  const label = React.useMemo(() => {
    if (labelProp) {
      return labelProp;
    }

    if (labelId) {
      return intl.formatMessage({ id: labelId, defaultMessage }, labelParameters);
    }

    if (type === 'checkbox') {
      return null;
    }

    return '';
  }, [labelProp, labelId, defaultMessage, labelParameters, type, intl]);

  const tooltip = React.useMemo(
    () => {
      if (formItemProps?.tooltip) {
        // The `div` wrapper around the `span` is needed; otherwise, the classname is overwritten by Ant
        return { title: formItemProps.tooltip, icon: <div><span className="fb-icon--info" /></div>, };
      }

      return undefined;
    },
    [formItemProps]
  );

  const displayError = React.useCallback(
    meta => {
      if (!meta.dirtySinceLastSubmit && meta.submitError) {
        return meta.submitError;
      }
      return validateOnInit ? meta.error : meta.touched && meta.error;
    },
    [validateOnInit]
  );

  const hasError = React.useCallback(meta => displayError(meta) && ((meta.error || meta.submitError)?.length > 0), [displayError]);

  const getErrorMessage = React.useCallback(
    meta => {
      if (!hideError) {
        if (error) {
          return intl.formatMessage({ id: error, defaultMessage: error });
        }
        if (displayError(meta)) {
          const message = meta.error || meta.submitError;
          return intl.formatMessage({ id: message, defaultMessage: message }, errorParameters);
        }
        return undefined;
      }
      return undefined;
    },
    [intl, hideError, error, errorParameters, displayError]
  );

  return (
    !hidden && (
      <Field
        name={name}
        type={type}
        validateFields={validateFields}
        validate={validate}
        initialValue={initialValue}
        subscription={subscription}
        value={value}
        format={format}
        formatOnBlur={formatOnBlur}
        parse={parse}
        className={className}
        allowNull={allowNull}
      >
        {({ input, meta }) => (
          <Form.Item
            label={label && <Ellipsis>{label}</Ellipsis>}
            help={getErrorMessage(meta)}
            validateStatus={hasError(meta) || error ? 'error' : ''}
            required={required}
            {...formItemProps}
            tooltip={tooltip}
          >
            {children({
              ...input,
              ...(disabled && { disabled }),
              onChange: e => {
                input.onChange(e);

                if (typeof inputOnChange === 'function') {
                  inputOnChange(e);
                }
              },
            })}
          </Form.Item>
        )}
      </Field>
    )
  );
};

export default FormField;
